import { createSlice } from "@reduxjs/toolkit";
import { getState } from "@bd-stores/store";

/**
 * @type {BD_StoreGameProgress}
 */
const initialState = {
    players: [],
    finalTeamAnswers: {},
    teamAnswers: {},
    roomStatus: {},
    isUsingTobyHint: {},
    allRoomCompleted: false,
    startedAt: null,
    shouldFinishAt: null,
    allocatedBudget: null,
    spendingBudget: null,
    spending: {},
    finishedAt: null,
    isFinished: false,
    isMiddleState: false,
    isOverBudget: false,
    hints: {},
};

const gameProgressSlice = createSlice({
    name: "gameProgress",
    initialState,
    reducers: {
        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['players']>} action
         */
        setPlayers: (state, action) => ({
            ...state,
            players: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['finalTeamAnswers']>} action
         */
        setFinalTeamAnswers: (state, action) => ({
            ...state,
            finalTeamAnswers: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['teamAnswers']>} action
         */
        setTeamAnswers: (state, action) => ({
            ...state,
            teamAnswers: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['roomStatus']>} action
         */
        setRoomStatus: (state, action) => ({
            ...state,
            roomStatus: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['isUsingTobyHint']>} action
         */
        setIsUsingTobyHint: (state, action) => ({
            ...state,
            isUsingTobyHint: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['allRoomCompleted']>} action
         */
        setAllRoomCompleted: (state, action) => ({
            ...state,
            allRoomCompleted: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['startedAt']>} action
         */
        setStartedAt: (state, action) => ({
            ...state,
            startedAt: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['shouldFinishAt']>} action
         */
        setShouldFinishAt: (state, action) => ({
            ...state,
            shouldFinishAt: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['finishedAt']>} action
         */
        setFinishedAt: (state, action) => ({
            ...state,
            finishedAt: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['isFinished']>} action
         */
        setFinished: (state, action) => ({
            ...state,
            isFinished: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['isMiddleState']>} action
         */
        setIsMiddleState: (state, action) => ({
            ...state,
            isMiddleState: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['allocatedBudget']>} action
         */
        setAllocatedBudget: (state, action) => ({
            ...state,
            allocatedBudget: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['spendingBudget']>} action
         */
        setSpendingBudget: (state, action) => ({
            ...state,
            spendingBudget: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<Object>} action
         */
        setSpending: (state, action) => ({
            ...state,
            spending: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['isOverBudget']>} action
         */
        setIsOverBudget: (state, action) => ({
            ...state,
            isOverBudget: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['hints']>} action
         */
        setHintIsOpened: (state, action) => ({
            ...state,
            hints: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<{roomId: string, incidentCost?: number, actionCost?: number, processedQuestion?: string, budgetAppliedIncident?: string, budgetAppliedFeedback?: string}>} action
         */
        updateRoomSpending: (state, action) => {
            const {
                roomId,
                incidentCost,
                actionCost,
                processedQuestion,
                budgetAppliedIncident,
                budgetAppliedFeedback,
            } = action.payload;
            const currentRoomSpending = state.spending[roomId] || {
                incidentCosts: [],
                actionCosts: [],
                processedQuestions: [],
                budgetAppliedIncidents: [],
                budgetAppliedFeedbacks: [],
            };

            const incidentCosts = Array.isArray(
                currentRoomSpending.incidentCosts,
            )
                ? currentRoomSpending.incidentCosts
                : [];
            const actionCosts = Array.isArray(currentRoomSpending.actionCosts)
                ? currentRoomSpending.actionCosts
                : [];
            const processedQuestions = Array.isArray(
                currentRoomSpending.processedQuestions,
            )
                ? currentRoomSpending.processedQuestions
                : [];
            const budgetAppliedIncidents = Array.isArray(
                currentRoomSpending.budgetAppliedIncidents,
            )
                ? currentRoomSpending.budgetAppliedIncidents
                : [];
            const budgetAppliedFeedbacks = Array.isArray(
                currentRoomSpending.budgetAppliedFeedbacks,
            )
                ? currentRoomSpending.budgetAppliedFeedbacks
                : [];

            let updatedRoomSpending = {
                incidentCosts: [...incidentCosts],
                actionCosts: [...actionCosts],
                processedQuestions: [...processedQuestions],
                budgetAppliedIncidents: [...budgetAppliedIncidents],
                budgetAppliedFeedbacks: [...budgetAppliedFeedbacks],
            };

            if (incidentCost !== undefined) {
                updatedRoomSpending.incidentCosts.push(incidentCost);
            }

            if (actionCost !== undefined) {
                updatedRoomSpending.actionCosts.push(actionCost);
            }

            if (processedQuestion !== undefined) {
                updatedRoomSpending.processedQuestions.push(processedQuestion);
            }

            if (
                budgetAppliedIncident !== undefined &&
                !budgetAppliedIncidents.includes(budgetAppliedIncident)
            ) {
                updatedRoomSpending.budgetAppliedIncidents.push(
                    budgetAppliedIncident,
                );
            }

            if (
                budgetAppliedFeedback !== undefined &&
                !budgetAppliedFeedbacks.includes(budgetAppliedFeedback)
            ) {
                updatedRoomSpending.budgetAppliedFeedbacks.push(
                    budgetAppliedFeedback,
                );
            }

            return {
                ...state,
                spending: {
                    ...state.spending,
                    [roomId]: updatedRoomSpending,
                },
            };
        },
    },
});

export const getRoomStatus = (roomId) => {
    const roomStatus = getState().gameProgress.roomStatus;
    return roomStatus?.[roomId] || "is-status-open";
};

export const getFinalTeamAnswers = (roomId) => {
    const finalTeamAnswers = getState().gameProgress.finalTeamAnswers;
    return finalTeamAnswers?.[roomId] || false;
};

export const getTeamAnswers = (roomId) => {
    const teamAnswers = getState().gameProgress.teamAnswers;
    return teamAnswers?.[roomId] || false;
};

export const getHintIsOpened = (roomId) => {
    const hintIsOpened = getState().gameProgress.hints;
    return hintIsOpened?.[roomId] || {};
};

export const getPlayers = () => {
    const players = getState().gameProgress.players;
    return players ?? [];
};

export const getUserFromId = (userId) => {
    const players = getState().gameProgress.players;
    const user = players.find((p) => p.userId === userId);
    return user;
};

export const getIsUsingTobyHint = (roomId) => {
    const isUsingTobyHint = getState().gameProgress.isUsingTobyHint;
    return isUsingTobyHint?.[roomId] || false;
};

export const getRoomSpending = (roomId) => {
    const spending = getState().gameProgress.spending || {};
    const roomSpending = spending[roomId];

    const incidentCosts = Array.isArray(roomSpending?.incidentCosts)
        ? roomSpending.incidentCosts
        : [];
    const actionCosts = Array.isArray(roomSpending?.actionCosts)
        ? roomSpending.actionCosts
        : [];

    const budgetAppliedIncidents =
        roomSpending && Array.isArray(roomSpending["budgetAppliedIncidents"])
            ? roomSpending["budgetAppliedIncidents"]
            : [];
    const budgetAppliedFeedbacks =
        roomSpending && Array.isArray(roomSpending["budgetAppliedFeedbacks"])
            ? roomSpending["budgetAppliedFeedbacks"]
            : [];

    return {
        incidentCosts: incidentCosts,
        actionCosts: actionCosts,
        budgetAppliedIncidents: budgetAppliedIncidents,
        budgetAppliedFeedbacks: budgetAppliedFeedbacks,
        totalIncidentCost: incidentCosts.reduce((sum, cost) => sum + cost, 0),
        totalActionCost: actionCosts.reduce((sum, cost) => sum + cost, 0),
        totalSpending:
            incidentCosts.reduce((sum, cost) => sum + cost, 0) +
            actionCosts.reduce((sum, cost) => sum + cost, 0),
    };
};

export const {
    setPlayers,
    setTeamAnswers,
    setFinalTeamAnswers,
    setRoomStatus,
    setAllRoomCompleted,
    setFinished,
    setIsMiddleState,
    setIsUsingTobyHint,
    setStartedAt,
    setShouldFinishAt,
    setFinishedAt,
    setAllocatedBudget,
    setSpendingBudget,
    setSpending,
    setIsOverBudget,
    setHintIsOpened,
    updateRoomSpending,
} = gameProgressSlice.actions;
export const gameProgress = gameProgressSlice.reducer;
